import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionFilter, setActionFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [days, setDays] = useState(30);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    fetchAuditData();
  }, [page, actionFilter, actorFilter, days]);

  const fetchAuditData = async () => {
    const token = localStorage.getItem("admin_token");
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50, days });
      if (actionFilter) params.append("action", actionFilter);
      if (actorFilter) params.append("actor_email", actorFilter);

      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/audit/logs?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/audit/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setLogs(logsRes.data.logs);
      setTotalPages(logsRes.data.total_pages);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load audit logs");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      grant_credits: "bg-green-600",
      deduct_credits: "bg-orange-600",
      change_plan: "bg-blue-600",
      delete_user: "bg-red-600",
      disable_user: "bg-yellow-600",
    };
    return colors[action] || "bg-slate-600";
  };

  const formatAction = (action) => {
    return action.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Audit Log</h1>
        <p className="text-muted-foreground mt-1">Complete trail of all admin actions</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
            <p className="text-muted-foreground text-sm mb-2">Total Entries</p>
            <p className="text-3xl font-bold text-foreground">{stats.total_entries}</p>
          </div>
          <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
            <p className="text-muted-foreground text-sm mb-2">Action Types</p>
            <p className="text-3xl font-bold text-foreground">{Object.keys(stats.action_breakdown || {}).length}</p>
          </div>
          <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
            <p className="text-muted-foreground text-sm mb-2">Top Actor</p>
            <p className="text-lg font-bold text-foreground truncate">
              {stats.top_actors?.[0]?.email || "N/A"}
            </p>
            <p className="text-muted-foreground text-xs">{stats.top_actors?.[0]?.actions || 0} actions</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Actions</option>
            <option value="grant_credits">Grant Credits</option>
            <option value="deduct_credits">Deduct Credits</option>
            <option value="change_plan">Change Plan</option>
            <option value="delete_user">Delete User</option>
            <option value="disable_user">Disable User</option>
          </select>
          <input
            type="text"
            placeholder="Filter by admin email..."
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
            className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <button
            onClick={() => {
              setActionFilter("");
              setActorFilter("");
            }}
            className="px-4 py-2 bg-muted hover:bg-slate-600 text-foreground rounded-lg transition"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No audit logs found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Target</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Timestamp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-muted/30 transition">
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 ${getActionBadge(log.action)} text-foreground text-xs rounded-full`}>
                        {formatAction(log.action)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-foreground font-medium text-sm">{log.actor_email}</p>
                      <p className="text-muted-foreground text-xs">{log.ip_address}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-muted-foreground text-sm capitalize">{log.target_type}</p>
                      <p className="text-muted-foreground text-xs font-mono">{log.target_id?.substring(0, 12)}...</p>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground text-sm">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => {
                          setSelectedLog(log);
                          setShowDetail(true);
                        }}
                        className="px-3 py-1 bg-primary hover:bg-primary/90 text-foreground text-xs rounded transition"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-muted hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground rounded-lg transition"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 bg-muted hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground rounded-lg transition"
          >
            Next
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && selectedLog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-foreground">Audit Entry Details</h2>
              <button
                onClick={() => setShowDetail(false)}
                className="text-muted-foreground hover:text-foreground transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Action Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-muted-foreground text-sm mb-1">Action</p>
                  <p className="text-foreground font-semibold">{formatAction(selectedLog.action)}</p>
                </div>
                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-muted-foreground text-sm mb-1">Timestamp</p>
                  <p className="text-foreground font-semibold text-sm">
                    {new Date(selectedLog.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Actor */}
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-muted-foreground text-sm mb-2">Performed By</p>
                <p className="text-foreground font-semibold">{selectedLog.actor_email}</p>
                <p className="text-muted-foreground text-xs mt-1">IP: {selectedLog.ip_address}</p>
              </div>

              {/* Target */}
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-muted-foreground text-sm mb-2">Target</p>
                <p className="text-foreground capitalize">{selectedLog.target_type}</p>
                <p className="text-muted-foreground text-xs font-mono mt-1">{selectedLog.target_id}</p>
              </div>

              {/* Before/After */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-lg">
                  <p className="text-red-400 font-medium mb-3">Before</p>
                  <pre className="text-muted-foreground text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.before, null, 2)}
                  </pre>
                </div>
                <div className="bg-green-500/10 border border-green-500/20 p-4 rounded-lg">
                  <p className="text-green-400 font-medium mb-3">After</p>
                  <pre className="text-muted-foreground text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.after, null, 2)}
                  </pre>
                </div>
              </div>

              {/* Metadata */}
              {Object.keys(selectedLog.metadata || {}).length > 0 && (
                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-muted-foreground text-sm mb-2">Additional Info</p>
                  <pre className="text-muted-foreground text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
